<template>
  <div
    class="border border-neutral-alpha-300 p-3 md:p-6 max-w-md md:max-w-sm w-full"
    :class="{
      'border-neutral-alpha-650 p-3 md:p-10': product?.isPopular,
      'rounded-2xl': rounding === 'all',
      'rounded-l-2xl rounded-r-2xl md:rounded-l-2xl md:rounded-r-none': rounding === 'left',
      'rounded-l-2xl rounded-r-2xl md:rounded-r-2xl md:rounded-l-none': rounding === 'right',
    }"
  >
    <div class="w-full flex md:flex-col items-center p-2 md:p-0">
      <div class="md:text-center space-y-2 pr-5 md:pr-0 border-r border-neutral-alpha-100 md:border-r-0">
        <BaseText type="label" size="sm" class="uppercase">
          {{ product.tier }}
        </BaseText>
        <BaseText size="sm" class="text-neutral-alpha-650">
          {{ product.description }}
        </BaseText>
      </div>
      <!-- Divider -->
      <div class="hidden md:block h-px w-full bg-neutral-alpha-300 my-5" />
      <!-- Price -->
      <div class="card-child-container pl-5 md:pl-0 flex-shrink-0 w-32 md:w-auto" :style="{ height: !isMonthly ? '60px' : '30px' }">
        <div class="flex items-center gap-1">
          <BaseText type="display" size="2xs" weight="600">
            ${{ finalPrice }}
          </BaseText>
          <BaseText size="sm" class="text-neutral-alpha-650">
            /month
          </BaseText>
        </div>
        <div v-if="!isMonthly" class="flex md:items-center gap-1 fade-in">
          <MoneyCircleIcon class="money-icon flex-shrink-0" />
          <BaseText type="label" size="sm" class="text-white">
            Save ${{ product.annualSavings }} annually
          </BaseText>
        </div>
      </div>
    </div>
    <!-- CTA -->
    <div class="space-y-2">
      <button class="w-full mt-4 p-2 h-10 flex items-center justify-center transition-colors text-white" 
      style="border-radius: 10px; background-color: #1F69FF"
      @click="handleAction(product)">
        <BaseText type="heading" size="sm" class="whitespace-nowrap">
          Start 7-Day Free Trial
        </BaseText>
      </button>
    </div>
    <!-- Divider -->
    <div class="hidden md:block h-px w-full bg-neutral-alpha-300 my-5" />
    <div class="w-full flex flex-col mt-2 md:mt-0 bg-neutral-alpha-100 md:bg-transparent rounded-lg md:rounded-none">
      <!-- What's included expander button (mobile only) -->
      <button class="md:hidden w-full flex items-center gap-2 p-2 rounded-lg"
      @click="mobileShowIncluded = !mobileShowIncluded">
        <QuestionMarkCircleIcon class="text-neutral-alpha-600 flex-shrink-0" />
        <BaseText type="body" size="sm" class="text-left text-neutral-alpha-650 flex-grow">
          What's included?
        </BaseText>
        <ChevronIcon className="text-neutral-alpha-600" class="transform" style="transition: transform 150ms ease-in-out"
        :style="{ transform: mobileShowIncluded ? 'scaleY(-1)' : 'none' }" /> 
      </button>
      <!-- included products -->
      <div class="w-full flex flex-col px-3 md:px-0 overflow-hidden" style="transition: height 300ms ease-in-out"
      :class="mobileShowIncluded ? 'h-onboard-products md:h-auto' : 'h-0 md:h-auto'">
        <div class="pt-1 md:pt-0">
          <BaseText type="label" size="sm" class="text-white">
            {{ product.baseUserCount }} User{{ product.baseUserCount > 1 ? 's' : '' }}
          </BaseText>
          <BaseText size="sm" class="text-neutral-alpha-650">
            $20 per additional user
          </BaseText>
        </div>
        <div v-for="includedProduct in allProducts" :key="includedProduct.key"
        class="flex items-center w-full gap-3 mt-3"
        :class="{'not-included-product' : !isProductIncluded(includedProduct.key)}">
          <img class="w-7 h-7" :src="includedProduct.imgUrl">
          <BaseText class="flex-1">
            {{ includedProduct.label }}
          </BaseText>
          <div v-if="isProductIncluded(includedProduct.key)">
            <BaseText v-if="includedProduct.key === 'spyder'" size="sm" class="text-neutral-alpha-650">
              <span v-if="isMonthly">
                {{ product.baseSpyder?.monthly }} Brand{{ product.baseSpyder?.monthly > 1 ? 's' : '' }}
              </span>
              <span v-else>Unlimited</span>
            </BaseText>
            <BaseText v-else-if="includedProduct.key === 'lens'" size="sm" class="text-neutral-alpha-650">
              {{ product.baseLens }} Brand{{ product.baseLens > 1 ? 's' : '' }} (Unlimited Ad Spend)
            </BaseText>
            <CheckmarkIcon v-else class="text-neutral-alpha-650" />
          </div>
          <TagRemoveIcon v-else class="text-neutral-alpha-500" />
        </div>
        <div class="md:hidden w-full h-3"/>
      </div>
    </div>
  </div>
</template>

<script>
import CheckmarkIcon from '@/components/globals/Icons/CheckmarkIcon.vue'
import TagRemoveIcon from '@/components/globals/Icons/TagRemoveIcon.vue'
import MoneyCircleIcon from '@/components/globals/Icons/MoneyCircleIcon.vue'
import QuestionMarkCircleIcon from '@/components/globals/Icons/QuestionMarkCircleIcon.vue'
import ChevronIcon from '@/components/globals/Icons/ChevronIcon.vue'

export default {
  name: 'DarkProductCard',
  components: {
    CheckmarkIcon,
    TagRemoveIcon,
    MoneyCircleIcon,
    QuestionMarkCircleIcon,
    ChevronIcon
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    isMonthly: {
      type: Boolean,
      default: false
    },
    allProducts: {
      type: Array,
      default: () => []
    },
    rounding: {
      type: String,
      default: 'all',
    }
  },
  data () {
    return {
      mobileShowIncluded: false
    }
  },
  computed: {
    isSaleActive () {
      return this.isMonthly && this.showDiscount
    },
    intervalType () {
      return this.isMonthly ? 'monthly' : 'annual'
    },
    finalPrice () {
      const prodPrice = this.product.price?.[this.intervalType]
      return this.isSaleActive && this.product.key !== 'basic-fp-plan' ? (prodPrice / 2).toFixed(0) : prodPrice
    }
  },
  methods: {
    handleAction (product) {
      const payload = {
        key: product.key,
        name: product.tier,
        description: product.description,
        price: this.finalPrice,
        interval: this.isMonthly ? 'month' : 'year',
        savingsPrice: product.annualSavings,
        includedUsers: product.baseUserCount,
        spyderCount: product.baseSpyder,
        lensCount: product.baseLens,
        includedProducts: product.includedProducts,
        priceId: product.priceId,
        addOn: product.addOns
      }
      this.$emit('action', payload)
    },
    isProductIncluded (key) {
      return this.product.includedProducts.includes(key)
    }
  }
}
</script>

<style scoped>
.not-included-product{
  filter: grayscale(100%);
  opacity: 0.4;
}
.money-icon{
  color: #EBBE7A
}

.card-child-container{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  transition: height 150ms ease-in-out;
}
.fade-in {
  animation: fadeIn 100ms ease-in;
}
@keyframes fadeIn {
  from { opacity: 0.25 }
  to { opacity: 1 }
}
</style>
